import React, { useState } from 'react'
import { ArrowRight, Store, Sparkles } from 'lucide-react'
import Logo from '../../assets/images/payhelm-logo-blue.png'
import { Button } from '@/features/shadcn/ui/button'
import OptionCard from './OptionCard'

export type OnboardingPath = 'store' | 'no_store'

interface PathChoiceProps {
  defaultValue?: OnboardingPath | null
  onSelect: (path: OnboardingPath) => void
}

/**
 * #693: onboarding entry screen. The single biggest fork is whether the whole
 * store-analytics suite applies (a store/marketplace OR a payment processor like
 * PayPal/Square — both unlock full analytics) or not (AI, WordPress site-tools,
 * marketing/traffic). The two paths then diverge into different follow-up steps.
 */
const PathChoice: React.FC<PathChoiceProps> = ({ defaultValue = null, onSelect }) => {
  const [selected, setSelected] = useState<OnboardingPath | null>(defaultValue)

  return (
    <div className="tw-fixed tw-inset-0 tw-max-h-screen tw-z-[10000] tw-flex tw-flex-col tw-overflow-auto tw-bg-slate-50">
      <div className="tw-mx-auto tw-w-full tw-max-w-2xl tw-px-4 tw-py-12 md:tw-py-16">
        <img src={Logo} className="tw-h-[44px] tw-w-auto tw-self-start tw-mb-10" fetchPriority="high" decoding="async" alt="PayHelm" />

        <div className="tw-text-sm tw-font-semibold tw-text-cyan-500 tw-mb-2">Welcome to PayHelm</div>
        <h1 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-slate-900 tw-mb-2">
          How will you use PayHelm?
        </h1>
        <p className="tw-text-slate-500 tw-mb-8">
          We'll tailor the setup to you. You can always connect a store or explore other tools later.
        </p>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4">
          <OptionCard
            horizontal
            selected={selected === 'store'}
            onClick={() => setSelected('store')}
            icon={<Store className="tw-h-5 tw-w-5" />}
            tile="tw-bg-indigo-50 tw-text-indigo-600"
            title="I have a store or take online payments"
            description="Shopify, Amazon, WooCommerce, PayPal, Square, and more — get full analytics, reports, inventory, and AI."
          />
          <OptionCard
            horizontal
            selected={selected === 'no_store'}
            onClick={() => setSelected('no_store')}
            icon={<Sparkles className="tw-h-5 tw-w-5" />}
            tile="tw-bg-blue-50 tw-text-blue-600"
            title="Not yet — I'm here for AI, my website, or marketing"
            description="Use the AI assistant, edit your WordPress site, and connect marketing & analytics tools. Add a store anytime."
          />
        </div>

        <div className="tw-mt-8">
          <Button
            className="tw-gap-2 tw-bg-indigo-600 hover:tw-bg-indigo-700"
            disabled={!selected}
            onClick={() => selected && onSelect(selected)}
          >
            Continue
            <ArrowRight className="tw-h-4 tw-w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PathChoice
